import { Component } from '@theme/component';
import {
  currentSequence,
  revealFallback,
  buildRankedGridURL,
  orderByRank,
  getConfig,
  buildResultAttributes,
  isConfigError,
  collectScopedCSS,
  stripSearchTracking,
  trackEvent,
} from '@theme/constructor-client';
import { itemFromCard, resultsLoaded, resultClicked } from '@theme/constructor-tracker';

const EAGER_CARD_COUNT = 4;

/**
 * Cards whose lead image is fetched at high priority. Two, not one: the mobile
 * grid is two columns, so the first row is two images and either can be the LCP
 * element — Lighthouse caught the second one at `fetchpriority=auto`.
 */
const HIGH_PRIORITY_CARD_COUNT = 2;

/**
 * Base class for every Constructor-ranked grid.
 *
 * Subclasses override exactly one method — `requestIds()` — and return an array
 * of Shopify product ids. A subclass cannot express markup, so the most damage
 * a wrong one can do is ask for the wrong products.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The element that receives the cards.
 * @property {HTMLElement} [editorNotice] - Theme-editor-only failure explanation.
 *
 * @extends {Component<Refs>}
 */
export class ConstructorGrid extends Component {
  /** Set once a real render has landed. Gates every destructive action. */
  hasRenderedSuccessfully = false;

  /**
   * `result_id` from the Constructor response that produced the current cards.
   *
   * This is the join key for attribution: it is what ties a later click or
   * conversion back to the ranking that surfaced the product. Subclasses set it
   * in `requestIds()`; nothing else may write it.
   * @type {string | null}
   */
  resultId = null;

  /**
   * Total results in the Constructor response, which is not the same as the
   * number of cards on screen. The spec asks for the total; reporting the page
   * size understates every result set. Subclasses set it in `requestIds()`.
   * @type {number | null}
   */
  totalResults = null;

  /**
   * The one <style> this component owns, holding the scoped colour rules that
   * came with the fetched cards. Replaced on every render, never appended to.
   *
   * It lives in <head>, not inside the component: a Section Rendering morph
   * reconciles the component's children against the server markup, which has no
   * such element, so anything kept inside is silently removed on the next
   * filter or sort.
   * @type {HTMLStyleElement | undefined}
   */
  #scopedStyles;

  /** How many cards are currently in the grid, across appended pages. */
  renderedCount = 0;

  /**
   * Aborts an in-flight card fetch when a newer one starts.
   * @type {AbortController | undefined}
   */
  #cardsFetch;

  /**
   * Every card placed by the render in progress, across both halves of a split
   * render, so the set is reported to Constructor once, as one result set.
   * @type {Element[]}
   */
  #renderedBatch = [];

  constructor() {
    super();
    // On the component itself, so it lives exactly as long as the cards do and
    // a card needs no listener of its own.
    this.addEventListener('click', this.#onResultClick);
  }

  /**
   * A stable key for abort and sequence bookkeeping.
   * @returns {string}
   */
  get surface() {
    return this.dataset.surface || this.localName;
  }

  /**
   * What failed, in merchant terms — a collection handle, a pod id or a search term.
   *
   * `handleFailure` used to read `this.dataset.handle`, which nothing ever
   * sets: browse emits `data-collection-handle` and recs `data-pod-id`. Every
   * fallback beacon therefore reported an empty handle, which is the one field
   * that says *which* surface broke.
   * @returns {string}
   */
  get beaconHandle() {
    return this.dataset.collectionHandle || this.dataset.podId || this.dataset.searchTerm || '';
  }

  /**
   * Which Constructor surface these cards belong to. Set on each card as
   * `data-cnstrc-item`, so search results are never reported as
   * recommendations.
   * @returns {string}
   */
  get itemContext() {
    return this.dataset.itemContext || 'search';
  }

  /**
   * Which container marker the beacon should see. Derived from `itemContext` so
   * a surface can never report itself as the wrong kind of result set.
   * @returns {'browse' | 'search' | 'recommendations'}
   */
  get beaconKind() {
    if (this.itemContext === 'browse') return 'browse';
    if (this.itemContext === 'recommendation') return 'recommendations';
    return 'search';
  }

  /**
   * Surface-specific container attributes — a browse grid reports which filter
   * produced it, a search grid its term. Subclasses override.
   * @returns {Record<string, string | number | undefined>}
   */
  beaconExtras() {
    return {};
  }

  /**
   * What Constructor needs to attribute a view or a click on this surface to
   * the response that ranked it. Subclasses add their own fields.
   * @returns {import('@theme/constructor-tracker').ResultContext}
   */
  trackingContext() {
    return {
      resultId: this.resultId,
      resultCount: this.totalResults ?? this.renderedCount,
      resultPage: 1,
      numResultsPerPage: this.renderedCount,
      url: location.href,
    };
  }

  /**
   * Reports one rendered result set — a first render or an appended page.
   * @param {Element[]} cards
   */
  reportResults(cards) {
    const items = cards.map(itemFromCard).filter((item) => item !== null);
    trackEvent(resultsLoaded(this.beaconKind, this.trackingContext(), items));
  }

  /** @param {MouseEvent} event */
  #onResultClick = (event) => {
    // Before Constructor has ranked the grid, its cards are Shopify's order and
    // no Constructor response can be credited with them.
    if (!this.hasRenderedSuccessfully || !(event.target instanceof Element)) return;

    // Links only: a wishlist heart, a swatch or quick add is not opening the
    // result, and nor are the gallery arrows, which sit inside the card's link.
    if (event.target.closest('button, input, label, select')) return;
    const link = event.target.closest('a[href]');
    const card = link?.closest('[data-cnstrc-item-id]');
    if (!card || !this.contains(card)) return;

    const item = itemFromCard(card);
    if (!item) return;

    const cards = [...this.querySelectorAll('[data-cnstrc-item-id]')];
    const context = {
      ...this.trackingContext(),
      strategyId: card.getAttribute('data-cnstrc-strategy-id') ?? undefined,
    };
    trackEvent(resultClicked(this.beaconKind, context, item, cards.indexOf(card)));
  };

  /**
   * Writes the container-level `data-cnstrc-*` attributes for the rendered set.
   *
   * Without these the beacon sees the cards but has nothing to attribute them
   * to: no result set marker and, above all, no `result_id`. Clicks and
   * conversions then reach Constructor unlinked to the response that ranked
   * them, so the ranking gets no credit and the model gets no feedback.
   *
   * @param {number} numResults - Emitted even when 0; a zero-result set still reports.
   */
  applyBeaconAttributes(numResults) {
    const attrs = buildResultAttributes({
      kind: this.beaconKind,
      numResults: this.totalResults ?? numResults,
      resultId: this.resultId,
      ...this.beaconExtras(),
    });

    for (const [name, value] of Object.entries(attrs)) {
      this.setAttribute(name, value);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  /**
   * Ask Constructor what to show. Subclasses must override.
   * @returns {Promise<(string | number)[]>}
   */
  async requestIds() {
    throw new Error(`${this.localName}: requestIds() not implemented`);
  }

  async load() {
    try {
      const ids = await this.requestIds();
      if (!ids?.length) {
        this.showEmpty();
        return;
      }
      await this.renderRanked(ids);
    } catch (error) {
      if (/** @type {any} */ (error)?.superseded) return;
      this.handleFailure(error);
    } finally {
      // Every exit path lifts the veil. A surface that hides server-rendered
      // content while it loads must never be able to leave it hidden.
      delete this.dataset.constructorPending;
    }
  }

  /**
   * Per-item beacon attributes.
   *
   * `data-cnstrc-item` has exactly one documented value — `recommendation`.
   * Search, browse and generic result items carry no item-type marker at all;
   * they are identified by being descendants of the typed container. A card
   * that labels itself `data-cnstrc-item="search"` reports a value the spec
   * does not define, which is why this is not simply `this.itemContext`.
   *
   * @see https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking
   *
   * @param {Element} card
   * @param {number} _index - Zero-based position within the rendered set.
   */
  /**
   * The variation Constructor returned for each ranked product, by product id. Filled by surfaces
   * that ask Constructor for results (constructor-results.js); empty on the others.
   * @type {Map<string, { variationId: string, color?: string, imageUrl?: string }>}
   */
  rankedVariations = new Map();

  /** Set by surfaces where cards should open on those variations (constructor-results.js). */
  prefersRankedVariations = false;

  decorateCard(card, _index) {
    if (this.itemContext === 'recommendation') {
      card.setAttribute('data-cnstrc-item', 'recommendation');
    }

    const productId = card.getAttribute('data-product-id');
    const variation = productId ? this.rankedVariations.get(productId) : undefined;
    if (variation && this.prefersRankedVariations) showRankedVariation(card, variation);
  }

  /**
   * Leading cards fetched and shown on their own, ahead of the rest of the
   * first render. 0 means one request for the whole set. Subclasses whose first
   * row is the page's LCP override this.
   * @returns {number}
   */
  get leadCount() {
    return 0;
  }

  /**
   * True while a split render has shown its lead cards and is still waiting for
   * the rest. Anything that appends — infinite scroll — must wait for it, or a
   * next page lands between the lead and the cards that belong after it.
   */
  pendingRest = false;

  /**
   * Fetch Liquid-rendered cards for `ids` and place them in the grid.
   * @param {(string | number)[]} ids
   */
  async renderRanked(ids, { append = false } = {}) {
    const seq = currentSequence(this.surface);
    const wanted = ids.slice(0, 48);
    const controller = this.#beginCardsFetch();

    this.#renderedBatch = [];

    const lead = append ? 0 : this.leadCount;
    if (lead > 0 && wanted.length > lead) {
      await this.#renderSplit(wanted, lead, seq, controller);
    } else {
      const cards = await this.#fetchCards(wanted, seq, controller);
      if (cards === null) return;
      this.#commit(cards, { append });
    }

    // A superseded render reports nothing; the render that replaced it will.
    if (this.#renderedBatch.length && currentSequence(this.surface) === seq) {
      this.reportResults(this.#renderedBatch);
    }
  }

  /**
   * The first render as two requests in parallel: the lead cards, which hold
   * the LCP image, and the rest. Shopify renders four cards in well under half
   * the time it takes for twenty-four, so the first row no longer waits on the
   * twenty below it.
   *
   * @param {(string | number)[]} wanted
   * @param {number} lead
   * @param {number} seq
   * @param {AbortController} controller
   */
  async #renderSplit(wanted, lead, seq, controller) {
    const leadIds = wanted.slice(0, lead);
    const restIds = wanted.slice(lead);

    const restRequest = this.#fetchCards(restIds, seq, controller);
    // Observed below; this only stops a rejection surfacing as unhandled while
    // the lead is still in flight.
    restRequest.catch(() => {});

    let leadCards;
    try {
      leadCards = await this.#fetchCards(leadIds, seq, controller);
    } catch (error) {
      // The whole render has failed; stop paying for its second half.
      controller.abort();
      throw error;
    }
    if (leadCards === null) return;

    // Nothing renderable at the top: fall back to whatever the rest holds.
    if (!leadCards.length) {
      const rest = await restRequest;
      if (rest !== null) this.#commit(rest, { append: false });
      return;
    }

    if (!this.#commit(leadCards, { append: false, lead: true })) return;

    this.pendingRest = true;
    delete this.dataset.constructorPending;

    try {
      let rest;
      try {
        rest = await restRequest;
      } catch {
        // One retry, without the prefetch: the lead just succeeded against the
        // same endpoint, so a lone failure here is most likely transient.
        rest = await this.#fetchCards(restIds, seq, this.#beginCardsFetch(), { prefetch: false });
      }
      if (rest === null) return;
      if (rest.length) this.#commit(rest, { append: true, offset: leadCards.length });
      else this.restUnavailable();
    } catch (error) {
      if (/** @type {any} */ (error)?.superseded) return;
      this.restUnavailable();
      this.handleFailure(error);
    } finally {
      this.pendingRest = false;
    }
  }

  /**
   * The rest of a split render could not be fetched. Subclasses that hold
   * placeholders for it release them here.
   */
  restUnavailable() {}

  /**
   * Decorate, place and record one batch of cards.
   *
   * @param {Element[]} cards
   * @param {{ append?: boolean, lead?: boolean, offset?: number }} options
   *   `offset` is the batch's position in the rendered set; `lead` marks the
   *   first half of a split render.
   * @returns {boolean} Whether the cards were placed.
   */
  #commit(cards, { append = false, lead = false, offset = 0 } = {}) {
    if (!cards.length) {
      this.showEmpty();
      return false;
    }

    cards.forEach((card, index) => {
      this.decorateCard(card, offset + index);
      prioritizeLeadImage(card, append && !offset ? EAGER_CARD_COUNT : offset + index);
    });

    if (!this.placeCards(cards, { append, lead })) {
      this.handleFailure(new Error('no placement target'));
      return false;
    }

    this.applyBeaconAttributes(cards.length);
    this.#renderedBatch.push(...cards);
    this.renderedCount = append ? this.renderedCount + cards.length : cards.length;
    this.hasRenderedSuccessfully = true;
    delete this.dataset.constructorEmpty;
    delete this.dataset.constructorFallback;
    this.dataset.constructorLoaded = 'true';

    this.dispatchEvent(
      new CustomEvent('constructor:rendered', {
        detail: { surface: this.surface, count: cards.length },
        bubbles: true,
      })
    );
    return true;
  }

  /**
   * Abort whatever card fetch is in flight and open a new one. One controller
   * per render, shared by both halves of a split render.
   * @returns {AbortController & { timedOut?: boolean }}
   */
  #beginCardsFetch() {
    this.#cardsFetch?.abort();
    const controller = /** @type {AbortController & { timedOut?: boolean }} */ (new AbortController());
    this.#cardsFetch = controller;
    return controller;
  }

  /**
   * One card fetch. Returns the cards, or null when the request was abandoned
   * (superseded or the component went away) and the caller should just stop.
   *
   * @param {(string | number)[]} ids
   * @param {number} seq
   * @param {AbortController & { timedOut?: boolean }} controller
   * @param {{ prefetch?: boolean }} [options]
   * @returns {Promise<Element[] | null>}
   */
  async #fetchCards(ids, seq, controller, { prefetch = true } = {}) {
    const url = buildRankedGridURL(ids);

    const early = prefetch ? await this.prefetchedCards(url) : null;
    if (early != null) return this.#parseCards(early, ids, seq);
    if (controller.signal.aborted) return null;

    // The client's timeout only covers the Constructor API call. Without one
    // here a stalled cards fetch leaves the surface on skeletons with aria-busy
    // set, forever, and never reaches handleFailure.
    const timer = setTimeout(() => {
      controller.timedOut = true;
      controller.abort();
    }, getConfig()?.timeoutMs ?? 8000);

    let html;
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        throw Object.assign(new Error(`cards fetch ${response.status}`), { status: response.status });
      }
      html = await response.text();
    } catch (error) {
      if (controller.timedOut) {
        throw Object.assign(new Error('cards fetch timed out'), { name: 'TimeoutError' });
      }
      // A deliberate abort — superseded by a newer render, or the component was
      // removed. Not a failure, and it must not fire a fallback beacon.
      // `null`, not a bare return: the caller tests `cards === null`, so
      // `undefined` falls straight through to `cards.length` and throws.
      if (controller.signal.aborted) return null;
      throw error;
    } finally {
      clearTimeout(timer);
    }

    return this.#parseCards(html, ids, seq);
  }

  /**
   * @param {string} html - A ranked-grid section response.
   * @param {(string | number)[]} ids
   * @param {number} seq
   * @returns {Element[] | null}
   */
  #parseCards(html, ids, seq) {
    // Re-check immediately before the DOM write. Checking once and then doing
    // several frames of async work is how a stale response ends up clobbering
    // a newer render.
    if (currentSequence(this.surface) !== seq) return null;

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const cards = orderByRank(this.extractCards(fragment), ids);
    this.adoptScopedStyles(fragment, cards);
    return cards;
  }

  /**
   * Card markup some earlier code already fetched for exactly `url`, or null.
   * Only the browse grid has a prefetch; every other surface fetches its own.
   * @param {URL} _url
   * @returns {Promise<string | null> | null}
   */
  prefetchedCards(_url) {
    return null;
  }

  /**
   * Pull only the cards out of the section response — the same approach
   * `paginated-list.js` uses for its own paging, so section chrome is ignored.
   *
   * The response comes from the search route, which stamps Shopify's
   * search-tracking parameters onto every product URL; they are removed here,
   * the one point every Constructor surface's cards pass through.
   *
   * @param {Document} fragment - The parsed section response
   * @returns {Element[]}
   */
  extractCards(fragment) {
    const grid = fragment.querySelector('[ref="grid"]');
    if (!grid) return [];
    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    for (const card of cards) removeSearchTracking(card);
    return cards;
  }

  /**
   * Carry over the scoped colour rules the fetched cards depend on.
   *
   * `extractCards` deliberately takes only the cards, which drops the section's
   * <style> blocks with them. That is correct for section chrome and wrong for
   * `contrast-override`: it scopes each card's colour tokens by `block.id`, and
   * block ids belong to the section that rendered them. A card fetched from
   * `search-results.liquid` therefore references a class the destination page
   * has never heard of, loses 16 custom properties, and falls back to the
   * section's own background — white cards render beige.
   *
   * The rules are written into one component-owned <style> that is replaced on
   * every render, so repeated fetches cannot accumulate stylesheets.
   *
   * @param {Document} fragment - The parsed section response.
   * @param {Element[]} cards
   */
  adoptScopedStyles(fragment, cards) {
    // One query over the fragment rather than one per card: every card in a
    // fetch shares the same block id, so this Set is almost always size 1.
    /** @type {Set<string>} */
    const scopes = new Set();
    for (const scoped of fragment.querySelectorAll('[class*="color-custom-"]')) {
      for (const cls of scoped.classList) if (cls.startsWith('color-custom-')) scopes.add(cls);
    }

    const sources = [...fragment.querySelectorAll('style')].map((el) => el.textContent ?? '');
    const css = collectScopedCSS(sources, scopes);

    // Nothing to carry is a normal outcome — a card with no custom background
    // emits no scoped rule at all. Clear any stylesheet left by a prior render
    // rather than leaving stale selectors behind.
    let sheet = this.#scopedStyles;
    if (!css) {
      sheet?.remove();
      this.#scopedStyles = undefined;
      return;
    }

    if (!sheet) {
      sheet = document.createElement('style');
      this.#scopedStyles = sheet;
      document.head.append(sheet);
    }
    // Every page of an infinite scroll produces the same CSS — the cards all
    // come from one section and one block id. Writing it again would reparse
    // the sheet and invalidate document style mid-scroll, for no change.
    if (sheet.textContent !== css) sheet.textContent = css;
  }

  /**
   * Put the cards on the page. Returns false when there is nowhere to put them.
   *
   * The default targets `ref="grid"`. Layouts whose markup the client must not
   * rebuild — a carousel, for instance — override this to fill existing slots
   * instead, so the surrounding component's own refs survive.
   *
   * @param {Element[]} cards
   * @param {{ append?: boolean }} [options] - Append for a next page rather than replacing.
   * @returns {boolean}
   */
  placeCards(cards, { append = false } = {}) {
    const { grid } = this.refs;
    if (!grid) return false;

    if (append) grid.append(...cards);
    else grid.replaceChildren(...cards);
    grid.removeAttribute('aria-busy');
    grid.hidden = false;
    return true;
  }

  /**
   * Zero results is a success, not a failure.
   *
   * It hides the grid rather than replacing it, because destroying the only
   * render target leaves the component unable to render anything ever again.
   */
  showEmpty() {
    this.hasRenderedSuccessfully = true;
    this.dataset.constructorEmpty = 'true';
    // A zero-result set is a reportable outcome, not an absence of one. An
    // empty recommendations pod, though, is not something a shopper viewed.
    this.applyBeaconAttributes(0);
    if (this.beaconKind !== 'recommendations') this.reportResults([]);
    const { grid } = this.refs;
    if (grid) {
      grid.hidden = true;
      grid.removeAttribute('aria-busy');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cardsFetch?.abort();
    this.#cardsFetch = undefined;
    this.#scopedStyles?.remove();
    this.#scopedStyles = undefined;
  }

  /** @param {any} error */
  handleFailure(error) {
    const stage = this.hasRenderedSuccessfully ? 'post_success' : 'initial';
    const reason = [error?.name, error?.message, error?.status].filter(Boolean).join(': ') || 'unknown';
    const misconfigured = isConfigError(error);

    revealFallback({
      surface: this.surface,
      stage,
      reason: misconfigured ? `config: ${reason}` : reason,
      handle: this.beaconHandle,
    });

    // An outage should degrade quietly — the server-rendered page is still a
    // real page. A misconfiguration should not: it fails on every load, for
    // everyone, and hiding the section leaves nobody anything to act on.
    if (misconfigured) {
      console.warn(`[constructor] ${this.surface}: ${reason}`);
      this.showEditorNotice(reason);
    }

    // A failure after a good render must never be destructive.
    if (stage === 'initial') {
      this.dataset.constructorFallback = 'true';
      const { grid } = this.refs;
      if (grid) grid.removeAttribute('aria-busy');
    }
  }

  /**
   * Explains an empty surface, in the theme editor only. The notice element is
   * rendered exclusively under `request.design_mode`, so a shopper can never
   * reach this — but a merchant who picks a pod that cannot work on this
   * template finds out immediately instead of staring at a gap.
   *
   * @param {string} reason
   */
  showEditorNotice(reason) {
    const { editorNotice } = this.refs;
    if (!(editorNotice instanceof HTMLElement)) return;
    editorNotice.textContent = `${editorNotice.dataset.prefix ?? ''} ${reason}`.trim();
    editorNotice.hidden = false;
    this.dataset.constructorNotice = 'true';
  }
}

/** Card attributes that carry a product URL. */
const PRODUCT_URL_ATTRIBUTES = ['href', 'data-product-url', 'data-url'];

/** @param {Element} card */
function removeSearchTracking(card) {
  for (const attribute of PRODUCT_URL_ATTRIBUTES) {
    for (const element of card.querySelectorAll(`[${attribute}*="_sid="]`)) {
      const value = element.getAttribute(attribute);
      if (value) element.setAttribute(attribute, stripSearchTracking(value));
    }
  }
}

function prioritizeLeadImage(card, position) {
  const image = card.querySelector('.card-gallery img');
  if (!(image instanceof HTMLImageElement)) return;

  if (position < HIGH_PRIORITY_CARD_COUNT) image.fetchPriority = 'high';
  if (position < EAGER_CARD_COUNT) return;

  image.loading = 'lazy';
  if (image.sizes && !image.sizes.startsWith('auto')) image.sizes = `auto, ${image.sizes}`;
}

/**
 * Open a fetched card on the variation Constructor ranked, before it is placed.
 *
 * The cards come from the search route, which knows nothing of the collection they're shown in, so
 * each opens on the product's default photo: gold, even on the Silver collection, where Constructor
 * ranks the silver variation (and its own preview shows it). This moves that colour's photo to the
 * front of the gallery and its swatch to the front of the swatches, marks the swatch chosen and
 * points the card's links at the variation.
 *
 * Moving the slide, rather than starting the slideshow on it with `initial-slide`: slideshow.js
 * sets its current index before scrolling, then skips the scroll because the slide "is already
 * current", so a visible slide past the first was marked but never shown.
 *
 * @param {Element} card - The grid item (`[ref="cards[]"]`).
 * @param {{ variationId: string, color?: string, imageUrl?: string }} variation
 */
function showRankedVariation(card, { variationId, color, imageUrl }) {
  const slideshow = card.querySelector('.card-gallery slideshow-component');
  if (!slideshow) return;
  const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));

  const inputs = Array.from(card.querySelectorAll('swatches-variant-picker-component input[data-option-media-id]'));
  const input =
    inputs.find((candidate) => candidate instanceof HTMLInputElement && candidate.dataset.variantId === variationId) ??
    (color ? inputs.find((candidate) => candidate instanceof HTMLInputElement && candidate.value === color) : undefined);

  // The colour's swatch names its photo. Without one (a single colour in stock shows no swatches),
  // find the slide whose image is the variation's own photo, by file name.
  let slide;
  if (input instanceof HTMLInputElement) {
    slide = slides.find((candidate) => candidate.getAttribute('slide-id') === input.dataset.optionMediaId);
  } else if (imageUrl) {
    const fileName = imageFileName(imageUrl);
    slide = slides.find((candidate) => {
      const image = candidate.querySelector('img');
      return image instanceof HTMLImageElement && imageFileName(image.getAttribute('src') ?? '') === fileName;
    });
  }
  if (!slide) return;

  if (slide !== slides[0]) moveSlideToFront(slideshow, slide);

  if (input instanceof HTMLInputElement) {
    for (const candidate of inputs) candidate.toggleAttribute('checked', candidate === input);
    // In front of the closed swatch stack too, so a silver card doesn't show a gold circle on top.
    // --i sets each circle's stacking order (snippets/variant-swatches.liquid), so it follows the
    // new order.
    const swatch = input.closest('li');
    const list = swatch?.parentElement;
    if (swatch && list) {
      list.prepend(swatch);
      Array.from(list.children).forEach((item, index) => {
        if (item instanceof HTMLElement && item.style.getPropertyValue('--i')) {
          item.style.setProperty('--i', String(index + 1));
        }
      });
    }
  }

  for (const link of card.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]')) {
    const href = link.getAttribute('href');
    if (!href) continue;
    const url = new URL(href, window.location.origin);
    url.searchParams.set('variant', variationId);
    link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  }

  // Constructor's click and view events should name the variation the shopper actually saw.
  if (card.hasAttribute('data-cnstrc-item-variation-id')) card.setAttribute('data-cnstrc-item-variation-id', variationId);
}

/**
 * Put `slide` first in its gallery. A slide that was hidden (past the card's three-slide cap) is
 * shown and the last visible one hidden in its place, so the dots still match the slides; slide
 * order variables and aria-hidden follow the new order.
 * @param {Element} slideshow
 * @param {Element} slide
 */
function moveSlideToFront(slideshow, slide) {
  const first = slideshow.querySelector('slideshow-slide');
  if (!first || first === slide) return;

  const wasHidden = slide.hasAttribute('hidden');
  first.before(slide);
  if (wasHidden) {
    slide.removeAttribute('hidden');
    const visible = Array.from(slideshow.querySelectorAll('slideshow-slide:not([hidden])'));
    visible.at(-1)?.setAttribute('hidden', '');
  }

  Array.from(slideshow.querySelectorAll('slideshow-slide')).forEach((candidate, index) => {
    if (candidate instanceof HTMLElement) candidate.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    candidate.setAttribute('aria-hidden', String(index !== 0));
  });
}

/**
 * The file name of a Shopify image URL, which is the same in Constructor's CDN URL
 * (cdn.shopify.com/s/files/.../files/NAME.png) and the theme's (/cdn/shop/files/NAME.png).
 * @param {string} url
 * @returns {string}
 */
function imageFileName(url) {
  return (url.split('?')[0] ?? '').split('/').pop() ?? '';
}
